import { KeyRound, MoreHorizontal, Pencil, Send, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { AnalyticsDestination } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardAction,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import { Label } from '@nucleus-commerce/ui/components/label';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useTestAnalyticsDestination } from '../hooks/use-analytics-destination-mutations';
import { providerConfig } from '../schemas/destination-form-schema';

import { DestinationStatusBadge } from './analytics-badges';
import { TestResult } from './test-result';

export type DestinationAction = 'edit' | 'rotate' | 'delete';

export interface DestinationCardProps {
  storeId: string;
  destination: AnalyticsDestination;
  /** Shows the switch and actions; false for read-only roles and closed stores. */
  editable: boolean;
  toggling: boolean;
  onToggle: (destination: AnalyticsDestination, active: boolean) => void;
  onAction: (action: DestinationAction, destination: AnalyticsDestination) => void;
}

function When({ value }: { value: string | null }) {
  const { t } = useTranslation('analytics');
  const format = useFormat();
  if (!value) return <span className="text-muted-foreground">{t('never')}</span>;
  return <span title={format.utc(value)}>{format.relative(value)}</span>;
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[10rem_1fr] gap-3 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}

function Code({ children }: { children: string }) {
  return <code className="font-mono text-[13px]">{children}</code>;
}

function ConfigRows({ destination }: { destination: AnalyticsDestination }) {
  const { t } = useTranslation('analytics');
  const known = providerConfig(destination);
  const testCode = (code: string | null) => (
    <Row label={t('fields.testEventCode')}>
      {code ? (
        <Code>{code}</Code>
      ) : (
        <span className="text-muted-foreground">{t('card.noTestCode')}</span>
      )}
    </Row>
  );
  switch (known.provider) {
    case 'meta_capi':
      return (
        <>
          <Row label={t('fields.pixelId')}>
            <Code>{known.config.pixelId}</Code>
          </Row>
          {testCode(known.config.testEventCode)}
        </>
      );
    case 'ga4_mp':
      return (
        <Row label={t('fields.measurementId')}>
          <Code>{known.config.measurementId}</Code>
        </Row>
      );
    case 'tiktok_events':
      return (
        <>
          <Row label={t('fields.pixelCode')}>
            <Code>{known.config.pixelCode}</Code>
          </Row>
          {testCode(known.config.testEventCode)}
        </>
      );
    case 'pinterest_capi':
      return (
        <>
          <Row label={t('fields.adAccountId')}>
            <Code>{known.config.adAccountId}</Code>
          </Row>
          <Row label={t('fields.testMode')}>
            {known.config.testMode ? t('card.testModeOn') : t('card.testModeOff')}
          </Row>
        </>
      );
  }
}

/** One connected platform: its settings, health, a pause switch, a test and the other actions. */
export function DestinationCard({
  storeId,
  destination,
  editable,
  toggling,
  onToggle,
  onAction,
}: DestinationCardProps) {
  const { t } = useTranslation('analytics');
  const message = useErrorMessage();
  const test = useTestAnalyticsDestination(storeId);
  const provider = t(`providers.${destination.provider}`);
  const switchId = `destination-active-${destination.id}`;

  return (
    <Card role="region" aria-label={provider} className="gap-4">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2 text-[15px]">
          {provider}
          <DestinationStatusBadge status={destination.status} />
        </CardTitle>
        {editable ? (
          <CardAction className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Switch
                id={switchId}
                checked={destination.status === 'active'}
                disabled={toggling}
                onCheckedChange={(checked) => onToggle(destination, checked)}
                aria-label={t('card.toggle', { provider })}
              />
              <Label htmlFor={switchId} className="text-muted-foreground text-sm font-normal">
                {destination.status === 'active' ? t('card.forwardingOn') : t('card.forwardingOff')}
              </Label>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label={t('actions.menu', { provider })}>
                  <MoreHorizontal aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-52"
                // The dialog opened from here focuses its own field; returning focus to the trigger
                // would take it away.
                onCloseAutoFocus={(event) => event.preventDefault()}
              >
                <DropdownMenuItem onSelect={() => onAction('edit', destination)}>
                  <Pencil aria-hidden />
                  {t('actions.edit')}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onAction('rotate', destination)}>
                  <KeyRound aria-hidden />
                  {t('actions.rotate')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  variant="destructive"
                  onSelect={() => onAction('delete', destination)}
                >
                  <Trash2 aria-hidden />
                  {t('actions.delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </CardAction>
        ) : null}
      </CardHeader>
      <CardContent className="grid gap-6 lg:grid-cols-2">
        <section aria-label={t('card.settings')} className="flex flex-col gap-2">
          <h3 className="text-sm font-medium">{t('card.settings')}</h3>
          <dl className="flex flex-col gap-2">
            <ConfigRows destination={destination} />
            <Row label={t(`fields.credential.${destination.provider}`)}>
              <span className="text-muted-foreground">{t('card.credentialStored')}</span>
            </Row>
          </dl>
        </section>
        <section aria-label={t('card.health')} className="flex flex-col gap-2">
          <h3 className="text-sm font-medium">{t('card.health')}</h3>
          <dl className="flex flex-col gap-2">
            <Row label={t('card.failures')}>
              <span
                className={cn('tabular-nums', destination.failureCount > 0 && 'text-destructive')}
              >
                {destination.failureCount}
              </span>
            </Row>
            <Row label={t('card.lastSuccess')}>
              <When value={destination.lastSuccessAt} />
            </Row>
            <Row label={t('card.lastFailure')}>
              <When value={destination.lastFailureAt} />
            </Row>
            <Row label={t('card.lastError')}>
              {destination.lastError ? (
                <details className="group">
                  <summary
                    className="text-destructive cursor-pointer truncate font-mono text-xs group-open:break-all group-open:whitespace-normal"
                    title={destination.lastError}
                  >
                    {destination.lastError}
                  </summary>
                </details>
              ) : (
                <span className="text-muted-foreground">{t('card.noError')}</span>
              )}
            </Row>
          </dl>
        </section>
      </CardContent>
      {editable ? (
        <CardFooter className="flex flex-col items-stretch gap-3">
          <div>
            <Button
              type="button"
              variant="outline"
              disabled={test.isPending}
              onClick={() => test.mutate(destination.id)}
            >
              <Send aria-hidden />
              {test.isPending ? t('actions.testing') : t('actions.test')}
            </Button>
          </div>
          {test.data ? <TestResult result={test.data} /> : null}
          {test.isError ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{message(test.error)}</AlertDescription>
            </Alert>
          ) : null}
        </CardFooter>
      ) : null}
    </Card>
  );
}
